import axios from 'axios';

/**
 * Stale-while-revalidate cache for data the dashboard shell needs on every page
 * (active modules, institute profile). Every page mounts its own DashboardLayout,
 * so without this each navigation refetched and the sidebar filled in piecemeal.
 *
 * - Last known value is available synchronously (memory, then sessionStorage)
 * - Concurrent requests for the same key share one network call
 * - Keys include the user and institute, so accounts never see each other's data
 * Menu visibility only; the backend still enforces module access.
 */
const PREFIX = 'coachhub_nav_cache:';
const memory = new Map();

export function readCached(key) {
  const entry = memory.get(key);
  if (entry && 'data' in entry) return entry;
  try {
    const stored = JSON.parse(sessionStorage.getItem(PREFIX + key) || 'null');
    if (stored && 'data' in stored) {
      memory.set(key, stored);
      return stored;
    }
  } catch { /* storage unavailable: fall back to the network */ }
  return null;
}

export function fetchShared(key, fetcher, { maxAge = 60000, force = false } = {}) {
  const entry = memory.get(key) || {};
  if (!force && entry.promise) return entry.promise;
  const cached = readCached(key);
  if (!force && cached && Date.now() - cached.ts < maxAge) return Promise.resolve(cached.data);
  const promise = fetcher()
    .then((data) => {
      const fresh = { data, ts: Date.now() };
      memory.set(key, fresh);
      try { sessionStorage.setItem(PREFIX + key, JSON.stringify(fresh)); } catch { /* optional */ }
      return data;
    })
    .catch((error) => {
      const current = memory.get(key) || {};
      delete current.promise;
      memory.set(key, current);
      throw error;
    });
  memory.set(key, { ...(readCached(key) || {}), promise });
  return promise;
}

export function clearNavCache() {
  memory.clear();
  try {
    Object.keys(sessionStorage).filter((k) => k.startsWith(PREFIX)).forEach((k) => sessionStorage.removeItem(k));
  } catch { /* optional */ }
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const profileCacheKey = (user) => `profile:${user?.id || 'anon'}:${user?.institute_id || ''}`;
export const modulesCacheKey = (user) => `modules:${user?.id || 'anon'}:${user?.institute_id || ''}:${user?.role || ''}`;

export const teacherPortalCacheKey = (user) => `teacher-portal:${user?.id || 'anon'}:${user?.institute_id || ''}`;

/** HRMS "show in teacher login" switches (own attendance / payslips), shared by sidebar and pages. */
export function fetchTeacherPortalVisibility(user, options) {
  return fetchShared(teacherPortalCacheKey(user), () => axios.get(`${API}/hrms/settings/portal-visibility`, { headers: authHeaders() }).then((res) => res.data), options);
}

/** One shared /institute/profile request for branding, category and practice settings. */
export function fetchInstituteProfile(user, options) {
  return fetchShared(profileCacheKey(user), () => axios.get(`${API}/institute/profile`, { headers: authHeaders() }).then((res) => res.data), options);
}

/** Active modules (+ role-level disabled modules) shared by the sidebar and dashboard. */
export function fetchActiveModules(user, options) {
  return fetchShared(modulesCacheKey(user), async () => {
    const res = await axios.get(`${API}/module-store/my-modules`, { headers: authHeaders() });
    const data = { active: res.data?.modules || {}, roleDisabled: [] };
    if (['student', 'teacher', 'parent'].includes(user?.role)) {
      try {
        const permRes = await axios.get(`${API}/institute/permissions/public`, { headers: authHeaders() });
        data.roleDisabled = (permRes.data?.modules || []).filter((m) => !m.enabled).map((m) => m.key);
      } catch { /* role toggles are optional */ }
      // Teaching Log is off until the institute turns it on; hide its menu items meanwhile.
      try {
        const tl = await axios.get(`${API}/teaching-log/features`, { headers: authHeaders() });
        if (!tl.data?.enabled) data.roleDisabled.push('teaching_log');
      } catch { data.roleDisabled.push('teaching_log'); }
    }
    return data;
  }, options);
}
